import type { PropsWithChildren, ReactElement } from "react";
import { createContext, useContext, useState } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  FormTableAddButton,
} from "@porchpass/ui";
import { SHEET_WIDTH_CLASSES } from "src/components/SlideUpModal/SlideUpModal";

type ButtonPopupCloseContextValue = {
  close: () => void;
};

const ButtonPopupCloseContext = createContext<ButtonPopupCloseContextValue>({
  close: () => undefined,
});

type ButtonPopupProps = {
  icon: ReactElement;
  text: string;
  children: ReactElement<ButtonPopupItemProps>[];
};

export const ButtonPopup = ({ icon, text, children }: ButtonPopupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const close = () => setIsOpen(false);

  const sheet = (
    <ButtonPopupCloseContext.Provider value={{ close }}>
      <Drawer
        open={isOpen}
        onOpenChange={setIsOpen}
        direction="bottom"
        dismissible
      >
        <DrawerContent className={SHEET_WIDTH_CLASSES}>
          <DrawerHeader className="text-left">
            <DrawerTitle>{text}</DrawerTitle>
          </DrawerHeader>
          <div className="flex flex-col gap-1 px-4 pb-4">
            {children ?? <div>No options</div>}
          </div>
        </DrawerContent>
      </Drawer>
    </ButtonPopupCloseContext.Provider>
  );

  return (
    <>
      <FormTableAddButton type="button" onClick={() => setIsOpen(true)}>
        {icon}
        {text ? text : "button"}
      </FormTableAddButton>
      {createPortal(sheet, document.body)}
    </>
  );
};

type ButtonPopupItemProps = {
  title: string;
  clickHandler: () => void;
} & PropsWithChildren;

export const ButtonPopupItem = ({
  title,
  clickHandler,
}: ButtonPopupItemProps) => {
  const { close } = useContext(ButtonPopupCloseContext);

  return (
    <Button
      type="button"
      variant="ghost"
      className="h-auto w-full justify-start! px-2 py-2 text-sm font-normal text-[#4a5466]"
      onClick={() => {
        clickHandler();
        close();
      }}
    >
      {title}
    </Button>
  );
};
